/**
 * The command menu (Ctrl+K or ⌘K): jump to any page of the menu, start the
 * common actions, or find an application by its number, owner or site.
 */
import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import { FileText, FilePlus2, Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { api, type SignedInUser, unwrap } from "@/api/client";
import { currentRole } from "@/auth/session";
import { IS_DESKTOP } from "@/desktop/mode";
import { useTheme } from "@/theme-context";

import { navGroups } from "./nav-items";

const ITEM =
  "flex min-h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm outline-none " +
  "data-[selected=true]:bg-muted data-[selected=true]:text-foreground";
const GROUP =
  "px-1 py-1.5 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 " +
  "[&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground";

function useApplicationSearch(text: string, enabled: boolean) {
  const query = text.trim();
  return useQuery({
    queryKey: ["command-menu", "applications", query],
    enabled: enabled && query.length >= 2,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/applications", { params: { query: { q: query, limit: 6 } } })),
  });
}

export function CommandMenu({
  session,
  open,
  onOpenChange,
}: {
  session: SignedInUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setChoice } = useTheme();
  const [text, setText] = useState("");
  const role = currentRole(session);
  const searchesApplications = !IS_DESKTOP && (role === "ARCHITECT" || role === "OFFICER");
  const found = useApplicationSearch(text, open && searchesApplications);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [open, onOpenChange]);

  const go = (to: string) => {
    onOpenChange(false);
    setText("");
    void navigate(to);
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label={t("command.label")}
      shouldFilter
      overlayClassName="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-fade-in"
      contentClassName="fixed left-1/2 top-[15vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-popover text-foreground shadow-2xl"
    >
      <Command.Input
        value={text}
        onValueChange={setText}
        placeholder={t(searchesApplications ? "command.placeholderApplications" : "command.placeholder")}
        className="h-12 w-full border-b border-border bg-transparent px-4 text-sm outline-none placeholder:text-muted-foreground"
      />
      <Command.List className="max-h-[60vh] overflow-y-auto p-1">
        <Command.Empty className="px-4 py-8 text-center text-sm text-muted-foreground">{t("command.empty")}</Command.Empty>
        {searchesApplications && (found.data?.items.length ?? 0) > 0 ? (
          <Command.Group heading={t("command.applications")} className={GROUP}>
            {found.data?.items.map((item) => (
              <Command.Item
                key={item.id}
                value={`${item.number ?? ""} ${item.ownerName} ${item.siteAddress} ${item.id}`}
                onSelect={() => { go(role === "OFFICER" ? `/review/${item.id}` : `/applications/${item.id}`); }}
                className={ITEM}
              >
                <FileText aria-hidden="true" className="size-4 text-muted-foreground" />
                <span className="font-medium">{item.number ?? t("command.draft")}</span>
                <span className="truncate text-muted-foreground">{item.ownerName} · {item.siteAddress}</span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
        {navGroups(session).map((group) => (
          <Command.Group key={group.label} heading={t(group.label)} className={GROUP}>
            {group.items.map(({ to, label, icon: Icon }) => (
              <Command.Item key={to} value={`${t(label)} ${to}`} onSelect={() => { go(to); }} className={ITEM}>
                <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
                {t(label)}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
        <Command.Group heading={t("command.actions")} className={GROUP}>
          {role === "ARCHITECT" || IS_DESKTOP ? (
            <Command.Item value={t("home.startCheck")} onSelect={() => { go("/pre-checks/new"); }} className={ITEM}>
              <FilePlus2 aria-hidden="true" className="size-4 text-muted-foreground" />
              {t("home.startCheck")}
            </Command.Item>
          ) : null}
          {([["light", Sun], ["dark", Moon], ["system", Monitor]] as const).map(([choice, Icon]) => (
            <Command.Item key={choice} value={`${t("app.theme.label")} ${t(`app.theme.${choice}`)}`} onSelect={() => { setChoice(choice); onOpenChange(false); }} className={ITEM}>
              <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
              {t("command.theme", { theme: t(`app.theme.${choice}`) })}
            </Command.Item>
          ))}
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
} // prettier-ignore
